import React from 'react';

import { Box, Tooltip, Typography } from '@mui/material';
import { WarningIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';

import {
  GENERATED_OUTSIDE_TOOLTIP,
  GENERATED_OUTSIDE_TOOLTIP_SLOT_PROPS,
} from '../../DisclosureDelivery.constants';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';
import {
  getIsGeneratedOutsideSignedTooltipVisible,
  getSentTooltip,
  getSignerStatusLabel,
} from '../../DisclosureDelivery.utils';
import ChannelBadge from '../ChannelBadge';
import DisclosureRecipientCopyButton from '../DisclosureRecipientCopyButton';
import DisclosureSignerActions from '../DisclosureSignerActions';

const REMEDIATION_TOOLTIP = 'Returned to sender — needs remediation';

const RECIPIENT_TYPE_LABEL = {
  PrimaryBorrower: 'Borrower',
  CoBorrower: 'Co-Borrower',
  NonBorrower: 'Non-Borrower',
  CompanyUser: 'Company User',
};

const EMPTY_CELL = '—';

const CompactDateCell = value => {
  if (!value) {
    return (
      <Typography color="text.secondary" variant="regular14">
        {EMPTY_CELL}
      </Typography>
    );
  }

  const date = dayjs(value);

  return (
    <Box className="flex flex-col">
      <Typography variant="regular14">{date.format('MMM D')}</Typography>
      <Typography color="text.secondary" variant="regular12">
        {date.format('h:mm A')}
      </Typography>
    </Box>
  );
};

const dateCell =
  key =>
  ({ [key]: value }) =>
    CompactDateCell(value);

const getBaseConfig = ({
  formBundleName,
  formDeliveryRequestId,
  isGeneratedOutside,
  isSignable,
}) => ({
  signerFullName: {
    label: 'Recipient',
    width: '16%',
    content: ({
      signerFullName,
      recipientType,
      channel,
      documentId,
      formBundleSignatureRequestItemId,
      isFenced,
      packageStatus,
      signedDate,
      status,
    }) => (
      <Box className="flex flex-col min-w-0">
        <Box className="flex items-center gap-4 min-w-0">
          <Typography data-truncated className="truncate min-w-0" variant="medium14">
            {signerFullName || '-'}
          </Typography>
          <Box className="shrink-0">
            <DisclosureRecipientCopyButton
              documentId={documentId}
              formBundleName={formBundleName}
              formDeliveryRequestId={formDeliveryRequestId}
              isFenced={isFenced}
              packageStatus={packageStatus}
              recipientRequestId={formBundleSignatureRequestItemId}
              signedDate={signedDate}
              signerFullName={signerFullName}
              status={status}
            />
          </Box>
        </Box>
        <Box className="flex items-center gap-4">
          {!!RECIPIENT_TYPE_LABEL[recipientType] && (
            <Typography color="text.secondary" variant="regular12">
              {RECIPIENT_TYPE_LABEL[recipientType]}
            </Typography>
          )}
          <ChannelBadge channel={channel} />
        </Box>
      </Box>
    ),
    sort: true,
  },
  status: {
    label: 'Status',
    width: '11%',
    content: ({ status, requiresRemediation, consentedDate }) => {
      const { title, variant } = getSignerStatusLabel(status, { isSignable, consentedDate });
      const statusLabel = <LabelCDS size={LabelSize.S} title={title} variant={variant} />;
      const showGeneratedOutsideTooltip = getIsGeneratedOutsideSignedTooltipVisible({
        status,
        isGeneratedOutside,
      });

      return (
        <Box className="flex items-center gap-4">
          {showGeneratedOutsideTooltip ? (
            <Tooltip
              arrow
              slotProps={GENERATED_OUTSIDE_TOOLTIP_SLOT_PROPS}
              title={GENERATED_OUTSIDE_TOOLTIP}
            >
              <Box className="flex">{statusLabel}</Box>
            </Tooltip>
          ) : (
            statusLabel
          )}
          {!!requiresRemediation && (
            <Tooltip arrow title={REMEDIATION_TOOLTIP}>
              <Box className="flex" data-testid={DisclosureDeliveryTestIds.SIGNER_REMEDIATION_ICON}>
                <WarningIcon size={14} />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    },
    sort: true,
  },
  generatedDate: {
    label: 'Generated',
    width: '11%',
    content: ({ generatedDate, sentDate }) => CompactDateCell(generatedDate ?? sentDate),
    tooltip: 'The disclosures were generated',
  },
  sentDate: {
    label: 'Sent',
    width: '11%',
    content: item => (
      <Tooltip arrow title={getSentTooltip(item)}>
        <Box>{CompactDateCell(item.sentDate)}</Box>
      </Tooltip>
    ),
    tooltip: 'When the disclosures were sent to the recipient',
    sort: true,
  },
  openedDate: {
    label: 'Opened',
    width: '11%',
    content: dateCell('openedDate'),
    tooltip: 'The recipient opened the email',
    sort: true,
  },
  consentedDate: {
    label: 'Read',
    width: '11%',
    content: dateCell('consentedDate'),
    tooltip: 'The recipient viewed the document',
    sort: true,
  },
  signedDate: {
    label: 'E-Signed',
    width: '11%',
    content: dateCell('signedDate'),
    tooltip: 'The recipient signed the documents',
    sort: true,
  },
  acknowledgedDate: {
    label: 'Acknowledged',
    width: '11%',
    content: dateCell('acknowledgedDate'),
    sort: true,
  },
});

export const getSignerTableConfig = ({
  enableRowActions,
  formBundleName = null,
  formDeliveryRequestId,
  isGeneratedOutside,
  isSignable = true,
  onEditDates,
}) => {
  const baseConfig = getBaseConfig({
    formBundleName,
    formDeliveryRequestId,
    isGeneratedOutside,
    isSignable,
  });

  if (!enableRowActions) {
    return baseConfig;
  }

  return {
    ...baseConfig,
    actions: {
      label: '',
      width: '7%',
      content: item => (
        <DisclosureSignerActions
          formDeliveryRequestId={formDeliveryRequestId}
          isGeneratedOutside={isGeneratedOutside}
          signerItem={item}
          onEditDates={onEditDates}
        />
      ),
    },
  };
};
